import {
  ArdoqId,
  APIPresentationAssetAttributes,
  APISlideAttributes,
  ViewIds,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { DoqType } from '@ardoq/doq';
import { trackClickedKBArticleLink } from 'admin/migrationChecklist/tracking';
import { migrationChecklist$ } from 'admin/migrationChecklist/migrationChecklist$';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import {
  type SlideInfo,
  slideOperations,
} from 'streams/slides/slideOperations';
import { renderMainPresentationView } from 'presentation/actions';
import { selectSlide } from 'presentation/slideNavigator/actions';
import { dispatchAction } from '@ardoq/rxbeach';
import { intersectionWith } from 'lodash';
import type { MetaInfo } from 'streams/views/mainContent/types';
import {
  type PresentationSlideViewInfo,
  ViewModifier,
} from 'viewDeprecation/types';
import {
  DISCONTINUED_VIEW_SETTINGS,
  getLeftMenuConfig,
} from 'viewDeprecation/consts';
import { updatePresentation } from 'streams/presentations/actions';
import { slideNamespace } from 'streams/slides/actions';
import { viewSettingsConsts } from '@ardoq/view-settings';
import { deleteRequested } from 'streams/crud/actions';

export const withDeprecatedViews = <T extends { slides: ArdoqId[] }>(
  presentations: T[],
  slidesById: Record<ArdoqId, APISlideAttributes>,
  deprecatedViews: MetaInfo[]
): (T & { deprecatedViews: MetaInfo[] })[] =>
  presentations.map(presentation => ({
    ...presentation,
    deprecatedViews: intersectionWith(
      deprecatedViews,
      (presentation.slides ?? [])
        .map(slideId => slidesById[slideId])
        .filter(isTruthy),
      ({ id }, slide) => slideOperations.getSlideViewId(slide) === id
    ),
  }));

export const hasDeprecatedSlides = (
  slides: APISlideAttributes[],
  deprecatedViews: MetaInfo[]
): boolean =>
  intersectionWith(
    slides,
    deprecatedViews,
    (slide, { id }) => slideOperations.getSlideViewId(slide) === id
  ).length > 0;

export const getDeprecatedSlidesInfo = (
  slideInfos: SlideInfo[],
  deprecatedViews: MetaInfo[]
): PresentationSlideViewInfo[] =>
  slideInfos
    .map(slideInfo => {
      const deprecatedView = deprecatedViews.find(
        ({ id }) => id === slideInfo.viewId
      );
      return deprecatedView?.viewStatus
        ? {
            ...slideInfo,
            viewStatus: deprecatedView.viewStatus,
            killDate: deprecatedView.killDate ?? null,
            viewId: deprecatedView.id,
          }
        : null;
    })
    .filter(isTruthy);

export const getDoqTypeForView = (viewId?: ViewIds) => {
  switch (viewId) {
    case ViewIds.RELATIONSHIPS:
      return DoqType.RELATIONSHIPS_GONE;
    case ViewIds.DEPMAP:
      return DoqType.DEPENDENCY_MAP_GONE;
    default:
      return DoqType.PAGE_GONE;
  }
};

export const openSlideMigrationKBArticle = () => {
  // eslint-disable-next-line ardoq-lint/no-direct-stream-state-access -- imperative one-shot click handler reused across migration checklist, slide deprecation banners, and dialogs; the analytics payload reads the current migration tab snapshot at click time
  trackClickedKBArticleLink(migrationChecklist$.state.activeMigrationId);
  window.open(KnowledgeBaseLink.MIGRATE_YOUR_DATA_TO_NEW_VIEWS, '_blank');
};

export const doDeleteSlide = (
  slideId: ArdoqId,
  presentation: APIPresentationAssetAttributes
) => {
  const allSlideIDs = presentation.slides ?? [];
  let nextSlideIndex = allSlideIDs.indexOf(slideId) + 1;

  // if it was the last slide and it was deleted, show the previous slide
  if (nextSlideIndex >= allSlideIDs.length) nextSlideIndex -= 2;
  const nextSlideID = allSlideIDs[nextSlideIndex];

  // delete the slide
  dispatchAction(
    updatePresentation({
      ...presentation,
      slides: presentation.slides.filter(value => {
        return value !== slideId;
      }),
    })
  );
  dispatchAction(deleteRequested(slideNamespace, slideId));

  // rerender the view
  dispatchAction(selectSlide(nextSlideID));
  dispatchAction(renderMainPresentationView());
};

type IsViewSettingDiscontinuedArgs = {
  viewId: ViewIds;
  settingId: string;
};

const isViewSettingDiscontinued = ({
  viewId,
  settingId,
}: IsViewSettingDiscontinuedArgs) =>
  !!DISCONTINUED_VIEW_SETTINGS.get(viewId)?.includes(settingId);

export const getViewModifierValuesForSlide = (
  viewId: ViewIds,
  viewState: Record<keyof typeof viewSettingsConsts, string>
) => {
  const viewModifiers: ViewModifier[] = [];
  // the leftMenuConfig is not too generic, adjust as the need arises
  const leftMenuConfig = getLeftMenuConfig(viewId, viewState);

  for (const entry of Object.entries(viewState)) {
    // we need to account for diverse structure of the view setting objects
    // this may vary across views, adjust as needed
    for (const settingsConfig of leftMenuConfig) {
      const settingValue = entry[0] === 'straightLines' ? !entry[1] : entry[1];

      if (settingValue) {
        // design: exclude falsy
        if (settingsConfig.id === entry[0]) {
          viewModifiers.push({
            valueLabel: `${settingsConfig.label} = ${String(settingValue)}`,
            isDiscontinued: isViewSettingDiscontinued({
              viewId,
              settingId: settingsConfig.id,
            }),
          });
        } else if (
          settingsConfig.id.includes('slider-') &&
          settingsConfig.options?.length !== 0
        ) {
          const settingsConfigOption = settingsConfig.options?.find(
            option => option.name === entry[0]
          );

          if (settingsConfigOption) {
            viewModifiers.push({
              valueLabel: `${settingsConfigOption.label} = ${String(
                settingValue
              )}`,
              isDiscontinued: isViewSettingDiscontinued({
                viewId,
                settingId: settingsConfigOption.name,
              }),
            });
          }
        }
      }
    }
  }

  return viewModifiers;
};
